import { useState } from "react";
import { useMutation } from "@apollo/client/react";
import { ExternalLink, Loader2, XCircle } from "lucide-react";
import { toast } from "sonner";

import { OPEN_BILLING_PORTAL } from "@/graphql/mutations/students";
import { Button } from "@/components/ui/button";

/**
 * The two ways out to Stripe's Customer Portal: straight to cancelling the
 * subscription (Directive 2023/2673 — cancelling must be one plain button), and
 * the portal's front page for the card, invoices and plan changes.
 *
 * The link is made when the button is pressed (`openBillingPortal`), never in
 * advance: a portal session is short-lived. Nothing is changed here — whatever
 * the parent does in the portal reaches the app through the webhook.
 */

function useBillingPortal() {
  const [openPortal] = useMutation(OPEN_BILLING_PORTAL);
  const [opening, setOpening] = useState<"cancel" | "manage" | null>(null);

  const open = async (cancel: boolean) => {
    setOpening(cancel ? "cancel" : "manage");
    try {
      const result = await openPortal({ variables: { cancel } });
      const payload = (result.data as any)?.openBillingPortal;
      if (payload?.url) {
        window.location.assign(payload.url);
        return;
      }
      toast.error(payload?.errors?.[0] ?? "Nepavyko atidaryti „Stripe“ puslapio");
    } catch {
      toast.error("Nepavyko atidaryti „Stripe“ puslapio");
    } finally {
      setOpening(null);
    }
  };

  return { open, opening };
}

export const CANCEL_LABEL = "Atšaukti prenumeratą";

export function CancelSubscriptionButton() {
  const { open, opening } = useBillingPortal();
  return (
    <Button variant="outline" onClick={() => open(true)} disabled={opening !== null}>
      {opening === "cancel" ? (
        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
      ) : (
        <XCircle className="mr-2 h-4 w-4" />
      )}
      {CANCEL_LABEL}
    </Button>
  );
}

export function ManageBillingButton({ variant = "ghost" }: { variant?: "ghost" | "outline" }) {
  const { open, opening } = useBillingPortal();
  return (
    <Button variant={variant} onClick={() => open(false)} disabled={opening !== null}>
      {opening === "manage" && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
      Tvarkyti prenumeratą ir kortelę
      <ExternalLink className="ml-2 h-3.5 w-3.5" />
    </Button>
  );
}

/** One sentence on where a Stripe subscription stands. */
export function subscriptionSentence(status: {
  cancelAtPeriodEnd: boolean;
  subscribedUntil: string | null;
}): string {
  const until = status.subscribedUntil ? ` ${status.subscribedUntil}` : "";
  return status.cancelAtPeriodEnd
    ? `Prenumerata atšaukta — ji galioja iki${until || " apmokėto laikotarpio pabaigos"}, daugiau mokesčių nebus.`
    : `Prenumerata atsinaujins automatiškai${until}. Atšaukti galite bet kada — apmokėtas laikotarpis galios iki pabaigos.`;
}
